import React, { useState, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Info } from 'lucide-react';
import './InfoTooltipButton.css';

export function InfoTooltipButton({ content }) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, isFlipped: false, arrowLeft: null });
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) return { top: 0, left: 0, isFlipped: false, arrowLeft: null };
    
    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current?.getBoundingClientRect();
    const tooltipWidth = tooltipRect?.width || 280; // Approximate max width fallback
    const tooltipHeight = tooltipRect?.height || 60; // Approximate height fallback
    const gap = 8;
    
    let top = rect.bottom + gap;
    // Original centered position (where tooltip would ideally be centered on trigger)
    const centeredLeft = rect.left + rect.width / 2;
    let left = centeredLeft;
    let isFlipped = false;
    
    // Check if tooltip would go off the right edge
    if (left + tooltipWidth / 2 > window.innerWidth - 16) {
      left = window.innerWidth - tooltipWidth / 2 - 16;
    }
    
    // Check if tooltip would go off the left edge
    if (left - tooltipWidth / 2 < 16) {
      left = tooltipWidth / 2 + 16;
    }
    
    // Check if tooltip would go off the bottom edge - flip to above trigger
    if (top + tooltipHeight > window.innerHeight - 16) {
      top = rect.top - tooltipHeight - gap;
      isFlipped = true;
    }
    
    // Calculate arrow position within tooltip (in pixels from left edge of tooltip)
    // The tooltip is centered on `left`, so its left edge is at `left - tooltipWidth/2`
    // The arrow should point to the trigger center (centeredLeft)
    // Arrow position = centeredLeft - (left - tooltipWidth/2) = centeredLeft - left + tooltipWidth/2
    let arrowLeft = centeredLeft - left + tooltipWidth / 2;
    
    // Clamp arrow position to stay within tooltip bounds (12px from edges minimum)
    const arrowMin = 12;
    const arrowMax = tooltipWidth - 12;
    arrowLeft = Math.max(arrowMin, Math.min(arrowMax, arrowLeft));
    
    return { top, left, isFlipped, arrowLeft };
  }, []);

  // Calculate position synchronously before paint
  useLayoutEffect(() => {
    if (isOpen) {
      setCoords(calculatePosition());
    }
  }, [isOpen, calculatePosition]);

  // Recalculate once the tooltip has mounted so we can use measured dimensions
  useLayoutEffect(() => {
    if (!isOpen || !tooltipRef.current) return;
    const rafId = requestAnimationFrame(() => {
      setCoords(calculatePosition());
    });

    return () => cancelAnimationFrame(rafId);
  }, [isOpen, calculatePosition]);

  // Update on scroll/resize
  useLayoutEffect(() => {
    if (!isOpen) return;

    const handleUpdate = () => {
      setCoords(calculatePosition());
    };

    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    
    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, calculatePosition]);

  const handleMouseEnter = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <>
      <div
        ref={triggerRef}
        className="info-tooltip-wrapper"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
      >
        <button
          type="button"
          className="info-tooltip-button"
          aria-label="More information"
          aria-describedby={isOpen ? 'info-tooltip' : undefined}
        >
          <Info size={16} />
        </button>
      </div>
      {isOpen && createPortal(
        <div
          ref={tooltipRef}
          id="info-tooltip"
          role="tooltip"
          className={`info-tooltip-content${coords.isFlipped ? ' info-tooltip-content--flipped' : ''}`}
          style={{
            top: coords.top,
            left: coords.left,
            '--arrow-left': coords.arrowLeft != null ? `${coords.arrowLeft}px` : '50%',
          }}
        >
          {content}
        </div>,
        document.body
      )}
    </>
  );
}

InfoTooltipButton.propTypes = {
  content: PropTypes.string.isRequired,
};
